/* eslint-disable @typescript-eslint/indent */
import { Button, FormControl, FormErrorMessage, FormLabel, Input, Textarea, useToast, VStack } from "@chakra-ui/react"
import { useFormik } from "formik"
import React, { FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import * as yup from "yup"
import Lazy from "yup/lib/Lazy"
import Reference from "yup/lib/Reference"
import { CreateIBeaconMutationVariables, useCreateIBeaconMutation } from "../graphql"

type CreateIBeaconFormValues = CreateIBeaconMutationVariables["input"]

const normalizeUuid = (uuid: string) => {
	const raw = uuid.replace(/-/g, "")

	if (raw.length === 32) {
		return `${raw.slice(0, 8)}-${raw.slice(8, 12)}-${raw.slice(12, 16)}-${raw.slice(16, 20)}-${raw.slice(20)}`
	}

	return uuid
}

const validationSchema = yup.object<Record<keyof CreateIBeaconFormValues, yup.AnySchema<any, any, any> | Reference<unknown> | Lazy<any, any>>>({
	label: yup.object({
		name: yup.string().required().label("Name"),
		description: yup.string().nullable().label("Description"),
	}),
	uuid: yup
		.string()
		.required()
		.matches(/^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{32})$/i, "Invalid UUID format")
		.label("UUID"),
	major: yup
		.number()
		.required()
		.min(0, "Major must be between 0 and 65535")
		.max(65535, "Major must be between 0 and 65535")
		.integer()
		.label("Major"),
	minor: yup
		.number()
		.required()
		.min(0, "Minor must be between 0 and 65535")
		.max(65535, "Minor must be between 0 and 65535")
		.integer()
		.label("Minor"),
	txPower: yup
		.number()
		.required()
		.min(-100, "TX Power must be between -100 and 20 dBm")
		.max(20, "TX Power must be between -100 and 20 dBm")
		.label("TX Power"),
})

export const CreateIBeaconForm: React.FC = () => {
	const initialValues: CreateIBeaconFormValues = {
		label: { name: "", description: "" },
		uuid: "",
		major: 0,
		minor: 0,
		txPower: 0,
	}

	const [{ fetching }, createIBeacon] = useCreateIBeaconMutation()
	const toast = useToast()
	const navigate = useNavigate()

	const onSubmit = async (values: CreateIBeaconFormValues) => {
		const { data, error } = await createIBeacon({ input: { ...values, uuid: normalizeUuid(values.uuid) } })

		if (error) {
			return toast({
				description: error.message.replace("[GraphQL] ", ""),
				status: "error",
			})
		}

		if (data?.createIBeacon) {
			toast({
				description: "iBeacon created successfully",
				status: "success",
			})
			navigate(`/ibeacons/${data.createIBeacon._id}`, { replace: true })

			return
		}
	}

	const formik = useFormik<CreateIBeaconFormValues>({ initialValues, validationSchema, onSubmit })

	return (
		<VStack as="form" onSubmit={(e) => formik.handleSubmit(e as unknown as FormEvent<HTMLFormElement>)} w="full" align="stretch" spacing={6}>
			<VStack w="full" align="stretch">
				<FormControl isInvalid={Boolean(formik.touched.label?.name && formik.errors.label?.name)} isRequired>
					<FormLabel fontWeight="bold">Name</FormLabel>

					<Input variant="filled" bgColor="grayscale.input-background" placeholder="Enter name" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("label.name")} />

					<FormErrorMessage>{formik.errors.label?.name}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.label?.description && formik.errors.label?.description)}>
					<FormLabel fontWeight="bold">Description</FormLabel>

					<Textarea variant="filled" bgColor="grayscale.input-background" placeholder="Enter description" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("label.description")} />

					<FormErrorMessage>{formik.errors.label?.description}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.uuid && formik.errors.uuid)} isRequired>
					<FormLabel fontWeight="bold">UUID</FormLabel>

					<Input variant="filled" bgColor="grayscale.input-background" placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" _placeholder={{ color: "grayscale.placeholer" }} {...formik.getFieldProps("uuid")} />

					<FormErrorMessage>{formik.errors.uuid}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.major && formik.errors.major)} isRequired>
					<FormLabel fontWeight="bold">Major</FormLabel>

					<Input
						type="number"
						min={0}
						max={65535}
						variant="filled"
						bgColor="grayscale.input-background"
						placeholder="Enter major"
						_placeholder={{ color: "grayscale.placeholer" }}
						value={formik.values.major}
						onChange={(e) => formik.setFieldValue("major", Number(e.target.value))}
					/>

					<FormErrorMessage>{formik.errors.major}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.minor && formik.errors.minor)} isRequired>
					<FormLabel fontWeight="bold">Minor</FormLabel>

					<Input
						type="number"
						min={0}
						max={65535}
						variant="filled"
						bgColor="grayscale.input-background"
						placeholder="Enter minor"
						_placeholder={{ color: "grayscale.placeholer" }}
						value={formik.values.minor}
						onChange={(e) => formik.setFieldValue("minor", Number(e.target.value))}
					/>

					<FormErrorMessage>{formik.errors.minor}</FormErrorMessage>
				</FormControl>

				<FormControl isInvalid={Boolean(formik.touched.txPower && formik.errors.txPower)} isRequired>
					<FormLabel fontWeight="bold">TX Power (dBm)</FormLabel>

					<Input
						type="number"
						min={-100}
						max={20}
						variant="filled"
						bgColor="grayscale.input-background"
						placeholder="Enter TX power"
						_placeholder={{ color: "grayscale.placeholer" }}
						value={formik.values.txPower}
						onChange={(e) => formik.setFieldValue("txPower", Number(e.target.value))}
					/>

					<FormErrorMessage>{formik.errors.txPower}</FormErrorMessage>
				</FormControl>
			</VStack>
			<Button type="submit" colorScheme="primary" isLoading={fetching}>
				Create
			</Button>
		</VStack>
	)
}
